/**
 * Case converter — pure, deterministic text transforms.
 *
 * Word-level cases (upper/lower/title/sentence/alternating) operate on the raw
 * text; programming cases (camel/pascal/snake/kebab/constant) tokenize first,
 * splitting on separators AND camelCase boundaries.
 */

export type CaseId =
  | "upper"
  | "lower"
  | "title"
  | "sentence"
  | "camel"
  | "pascal"
  | "snake"
  | "kebab"
  | "constant"
  | "alternating";

export function toUpper(s: string): string {
  return s.toUpperCase();
}

export function toLower(s: string): string {
  return s.toLowerCase();
}

export function toTitle(s: string): string {
  return s
    .split(/(\s+)/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join("");
}

export function toSentence(s: string): string {
  return s
    .toLowerCase()
    .replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, (c) => c.toUpperCase());
}

/** Split into words on separators and camelCase / CONSTANT boundaries. */
function splitWords(s: string): string[] {
  return s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);
}

export function toCamel(s: string): string {
  return splitWords(s)
    .map((w, i) =>
      i === 0
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join("");
}

export function toPascal(s: string): string {
  return splitWords(s)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("");
}

export function toSnake(s: string): string {
  return splitWords(s)
    .map((w) => w.toLowerCase())
    .join("_");
}

export function toKebab(s: string): string {
  return splitWords(s)
    .map((w) => w.toLowerCase())
    .join("-");
}

export function toConstant(s: string): string {
  return splitWords(s)
    .map((w) => w.toUpperCase())
    .join("_");
}

export function toAlternating(s: string): string {
  let i = 0;
  return [...s]
    .map((ch) => {
      if (/[a-zA-Z]/.test(ch)) {
        const out = i % 2 === 0 ? ch.toLowerCase() : ch.toUpperCase();
        i++;
        return out;
      }
      return ch;
    })
    .join("");
}

export const TRANSFORMS: Record<CaseId, (s: string) => string> = {
  upper: toUpper,
  lower: toLower,
  title: toTitle,
  sentence: toSentence,
  camel: toCamel,
  pascal: toPascal,
  snake: toSnake,
  kebab: toKebab,
  constant: toConstant,
  alternating: toAlternating,
};
